import { useState, type FormEvent } from 'react'
import { Mail, Lock, Eye, EyeOff, AlertCircle } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { KodigiloLogo } from '@/components/Logo'
import { useAuth } from './AuthProvider'
import { env } from '@/lib/env'

export default function LoginScreen() {
  const { signIn, error } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPw, setShowPw] = useState(false)
  const [loading, setLoading] = useState(false)

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setLoading(true)
    try {
      await signIn(email, password)
    } catch {
      // erro já está no ctx
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-bg relative overflow-hidden">
      {/* Decorative diagonal pattern bottom-right */}
      <DecorativePattern />

      <div className="absolute inset-0 flex flex-col items-center justify-center px-20">
        <div className="w-full max-w-[420px]">
          <div className="flex justify-center mb-3">
            <KodigiloLogo height={44} />
          </div>
          <p className="text-center text-base text-fg-muted mb-14 tracking-tight">
            Mapas mentais, diagramas e arquitetura — em um só canvas.
          </p>

          <form onSubmit={onSubmit} className="bg-surface border border-border rounded-xl p-8 shadow-sm">
            <h2 className="text-lg font-semibold tracking-tight mb-1">Entrar</h2>
            <p className="text-sm text-fg-muted mb-6">Use sua conta Kodigilo</p>

            {error && (
              <div className="flex items-start gap-2.5 px-3 py-2.5 mb-4 bg-error-soft text-error rounded-md text-sm">
                <AlertCircle size={16} className="mt-px shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <label className="block mb-3.5">
              <span className="block text-xs font-medium text-fg-muted mb-1.5">E-mail</span>
              <Input
                size="lg"
                type="email"
                leadingIcon={<Mail size={16} />}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                error={!!error}
                required
                autoComplete="email"
              />
            </label>
            <label className="block mb-5">
              <span className="block text-xs font-medium text-fg-muted mb-1.5">Senha</span>
              <Input
                size="lg"
                type={showPw ? 'text' : 'password'}
                leadingIcon={<Lock size={16} />}
                trailingIcon={
                  <button type="button" onClick={() => setShowPw((s) => !s)} className="flex">
                    {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                }
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                error={!!error}
                required
                autoComplete="current-password"
              />
            </label>

            <Button type="submit" variant="primary" size="lg" fullWidth disabled={loading}>
              {loading ? (
                <span className="inline-flex items-center gap-2.5">
                  <span className="w-3.5 h-3.5 rounded-full border-2 border-white/30 border-t-white animate-k-spin" />
                  Entrando…
                </span>
              ) : (
                'Entrar'
              )}
            </Button>

            <div className="mt-5 pt-5 border-t border-divider flex justify-between text-xs text-fg-muted">
              <span>v0.0.1 · MVP</span>
              <span>tenant: {env.VITE_FIREBASE_TENANT_ID}</span>
            </div>
          </form>

          <p className="text-center mt-6 text-xs text-fg-subtle">Ferramenta pessoal · single-user</p>
        </div>
      </div>
    </div>
  )
}

function DecorativePattern() {
  const lines: JSX.Element[] = []
  for (let i = 0; i < 14; i++) {
    const t = i / 13
    lines.push(
      <line key={`a${i}`} x1={2 + t * 20} y1={2 + t * 20} x2={4 + t * 20} y2={4 + t * 20} />,
      <line key={`b${i}`} x1={2 + t * 20} y1={22 - t * 20} x2={4 + t * 20} y2={20 - t * 20} />,
    )
  }
  return (
    <svg
      className="absolute -right-10 -bottom-10 pointer-events-none opacity-[0.06]"
      width={520}
      height={520}
      viewBox="0 0 24 24"
      stroke="#FF3B2F"
      strokeWidth="0.5"
      strokeLinecap="round"
    >
      {lines}
    </svg>
  )
}
